import React from 'react';
import {
    Control,
    FieldErrors,
    FieldValues,
    SubmitHandler,
    useForm, UseFormGetValues,
    UseFormHandleSubmit,
    UseFormRegister, UseFormReset,
    UseFormSetValue, UseFormTrigger, UseFormWatch,
    FormProvider as FormProviderHookForm,
} from 'react-hook-form';

export interface FormContext {
    register: UseFormRegister<FieldValues>;
    control: Control;
    handleSubmit: UseFormHandleSubmit<FieldValues>;
    errors: FieldErrors;
    setValue: UseFormSetValue<FieldValues>
    getValues: UseFormGetValues<FieldValues>
    reset: UseFormReset<FieldValues>
    trigger: UseFormTrigger<FieldValues>
    watch: UseFormWatch<FieldValues>
}

export interface FormProviderProps {
/**
* On submit, values from the child form components will be
* returned in the format:
* [key: string]: value (any) - instead of the native
* event.
* This makes it easy to further process the data or send it to an endpoint,
* without worrying about handling.
* */
    onSubmit: SubmitHandler<FieldValues>;
    children: React.ReactNode | React.ReactNode[] | string | null | undefined;
    values?: Partial<FieldValues>
    disabled?: boolean;
}

/**
 * This creates an instance of <form /> that you can use Primitive Input, Select, Checkbox, Radio, etc elements in.
 * It wraps form elements in a React Context and a form tag so that you can access values and error messages anywhere
 * within your form. React-Hook-Form is used underneath.
 * @alias FormProviderProps
 * */
const FormProvider = React.forwardRef<HTMLFormElement, FormProviderProps>(
    ({ children, onSubmit, values, disabled, ...rest }, ref) => {
        const props = useForm();
        const valueComparator = JSON.stringify(values);
        const { setValue } = props;
        React.useEffect(() => {
            if (values) {
                Object.entries(values).forEach(([k, v]) => {
                    setValue(k, v);
                });
            }
        // eslint-disable-next-line
        }, [valueComparator]);
        return (
            <FormProviderHookForm {...props}>
                <form
                    ref={ref}
                    onSubmit={props.handleSubmit(onSubmit)}
                    css={{
                        pointerEvents: disabled ? 'none' : 'auto',
                        opacity: disabled ? '0.7' : '1',
                    }}
                    {...rest}
                >
                    <fieldset
                        disabled={disabled}
                        aria-disabled={disabled}
                        /*
        * Little hack to make it WAI-ARIA and disable
        * for non-mouse users, without adding another layer of style
        * */
                        css={{ display: 'contents' }}
                    >
                        {children}
                    </fieldset>
                </form>
            </FormProviderHookForm>
        );
    },
);

FormProvider.displayName = 'FormProvider';

export default FormProvider;
